import { useEffect, useState } from 'react';
import {
  fetchMFAPreference,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchDevices,
  forgetDevice,
} from '@aws-amplify/auth';
import type { FetchDevicesOutput } from '@aws-amplify/auth';
import type { TrackedDevice } from '@bpd/shared';
import { useAuth } from '../../auth/useAuth.ts';
import { projectsApi } from '../../api/projects.ts';

type MfaState = 'loading' | 'error' | 'enabled' | 'not-enrolled' | 'enrolling';
type Device = FetchDevicesOutput[number];

function formatDate(d: Date | undefined) {
  return d ? d.toLocaleString() : '—';
}

function formatIsoDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleString() : '—';
}

/** Amplify's fetchDevices() `id` is Cognito's own DeviceKey (confirmed via its implementation —
 * it maps `DeviceKey` straight to `id`), the same identifier the "All Devices" section's
 * TrackedDevice rows key on. That means the friendly `label` an admin sets there (stored in this
 * app's own DEVICE_LABELS_TABLE overlay — Cognito's own device_name attribute is never set by
 * anything in this app, and would show "Unnamed device" otherwise) can be looked up here too,
 * instead of the Trusted Devices list silently ignoring it. */
function displayNameForDevice(d: Device, allDevices: TrackedDevice[] | null): string {
  const match = allDevices?.find((x) => x.deviceKey === d.id);
  return match?.label || match?.deviceName || d.name || 'Unnamed device';
}

export function SecurityPage() {
  const { user } = useAuth();

  const [mfaState, setMfaState] = useState<MfaState>('loading');
  const [setupUri, setSetupUri] = useState<string | null>(null);
  const [sharedSecret, setSharedSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  const [devices, setDevices] = useState<Device[] | null>(null);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [forgettingId, setForgettingId] = useState<string | null>(null);

  const [allDevices, setAllDevices] = useState<TrackedDevice[] | null>(null);
  const [allDevicesError, setAllDevicesError] = useState<string | null>(null);
  const [labelDrafts, setLabelDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [forgettingKey, setForgettingKey] = useState<string | null>(null);

  function loadMfaStatus() {
    setMfaState('loading');
    fetchMFAPreference()
      .then(({ enabled }) => setMfaState(enabled?.includes('TOTP') ? 'enabled' : 'not-enrolled'))
      .catch(() => setMfaState('error'));
  }

  function loadDevices() {
    setDevicesError(null);
    fetchDevices()
      .then(setDevices)
      .catch(() => setDevicesError('Could not load trusted devices.'));
  }

  function loadAllDevices() {
    setAllDevicesError(null);
    projectsApi
      .listAllDevices()
      .then((data) => {
        setAllDevices(data);
        setLabelDrafts(Object.fromEntries(data.map((d) => [d.deviceKey, d.label ?? ''])));
      })
      .catch(() => setAllDevicesError('Could not load devices.'));
  }

  useEffect(loadMfaStatus, []);
  useEffect(loadDevices, []);
  useEffect(loadAllDevices, []);

  async function startEnrollment() {
    setEnrollError(null);
    try {
      const details = await setUpTOTP();
      setSetupUri(details.getSetupUri('Blue Phoenix Designs', user?.email).toString());
      setSharedSecret(details.sharedSecret);
      setCode('');
      setMfaState('enrolling');
    } catch (e) {
      setEnrollError(e instanceof Error ? e.message : 'Could not start setup. Try again.');
    }
  }

  async function confirmEnrollment(e: React.FormEvent) {
    e.preventDefault();
    setEnrollError(null);
    setEnrolling(true);
    try {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setSetupUri(null);
      setSharedSecret(null);
      setMfaState('enabled');
    } catch (e) {
      setEnrollError(e instanceof Error ? e.message : 'Invalid code. Try again.');
    } finally {
      setEnrolling(false);
    }
  }

  async function handleForget(device: Device) {
    setForgettingId(device.id);
    try {
      await forgetDevice({ device });
      setDevices((prev) => prev?.filter((d) => d.id !== device.id) ?? prev);
    } catch {
      setDevicesError('Could not forget that device. Try again.');
    } finally {
      setForgettingId(null);
    }
  }

  async function handleSaveLabel(d: TrackedDevice) {
    const label = (labelDrafts[d.deviceKey] ?? '').trim();
    setSavingKey(d.deviceKey);
    setAllDevicesError(null);
    try {
      const { label: savedLabel } = await projectsApi.updateDeviceLabel(d.deviceKey, {
        label,
        ownerUsername: d.ownerUsername,
        ownerEmail: d.ownerEmail,
      });
      setAllDevices(
        (prev) =>
          prev?.map((x) => (x.deviceKey === d.deviceKey ? { ...x, label: savedLabel } : x)) ?? prev,
      );
    } catch {
      setAllDevicesError('Could not save that label. Try again.');
    } finally {
      setSavingKey(null);
    }
  }

  async function handleAdminForget(d: TrackedDevice) {
    setForgettingKey(d.deviceKey);
    setAllDevicesError(null);
    try {
      await projectsApi.adminForgetDevice(d.deviceKey, d.ownerUsername);
      setAllDevices((prev) => prev?.filter((x) => x.deviceKey !== d.deviceKey) ?? prev);
    } catch {
      setAllDevicesError('Could not forget that device. Try again.');
    } finally {
      setForgettingKey(null);
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">Security</h1>
      <p className="mt-2 text-sm text-gray-500">
        Two-factor authentication and trusted devices for the admin portal.
      </p>

      {/* Two-factor authentication */}
      <section className="mt-8 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h2>

        {mfaState === 'loading' && <p className="mt-3 text-sm text-gray-500">Loading…</p>}
        {mfaState === 'error' && (
          <p className="mt-3 text-sm text-red-700">
            Couldn&apos;t load your 2FA status. Try again in a moment.
          </p>
        )}

        {mfaState === 'enabled' && (
          <div className="mt-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-sm font-medium text-green-700">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Enabled
            </span>
            <p className="text-sm text-gray-500">
              A code from your authenticator app is required on any new or unrecognized device.
            </p>
          </div>
        )}

        {mfaState === 'not-enrolled' && (
          <div className="mt-3">
            <p className="text-sm text-gray-500">
              Not set up yet. Once enabled, signing in from a new device will require a code from an
              authenticator app (Google Authenticator, Authy, 1Password, Apple Passwords, etc.) —
              devices you&apos;ve already signed in from won&apos;t be affected.
            </p>
            <button
              type="button"
              onClick={() => void startEnrollment()}
              className="mt-3 rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
            >
              Set up two-factor authentication
            </button>
            {enrollError && <p className="mt-2 text-sm text-red-700">{enrollError}</p>}
          </div>
        )}

        {mfaState === 'enrolling' && setupUri && (
          <form onSubmit={(e) => void confirmEnrollment(e)} className="mt-3 space-y-3">
            <p className="text-sm text-gray-600">
              Scan this in your authenticator app, or enter the key manually:
            </p>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <a href={setupUri} className="break-all text-xs text-phoenix-blue-700 underline">
                {setupUri}
              </a>
              {sharedSecret && (
                <p className="mt-2 font-mono text-xs text-gray-500">Manual key: {sharedSecret}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Enter the 6-digit code from the app to confirm
              </label>
              <input
                type="text"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
                className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-phoenix-blue-500 focus:outline-none focus:ring-1 focus:ring-phoenix-blue-500"
                placeholder="123456"
              />
            </div>
            {enrollError && <p className="text-sm text-red-700">{enrollError}</p>}
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={enrolling}
                className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
              >
                {enrolling ? 'Confirming…' : 'Confirm & Enable'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSetupUri(null);
                  setSharedSecret(null);
                  setMfaState('not-enrolled');
                }}
                className="text-sm text-gray-500 hover:underline"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>

      {/* Trusted devices */}
      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Trusted Devices</h2>
        <p className="mt-1 text-sm text-gray-500">
          Devices that have already passed a two-factor check and won&apos;t be prompted again.
          Forget a device if it&apos;s lost, sold, or you just want it to require a code next time.
        </p>

        {devicesError && <p className="mt-3 text-sm text-red-700">{devicesError}</p>}
        {devices === null && !devicesError && (
          <p className="mt-3 text-sm text-gray-500">Loading…</p>
        )}
        {devices !== null && devices.length === 0 && (
          <p className="mt-3 text-sm text-gray-400">No remembered devices yet.</p>
        )}

        {devices !== null && devices.length > 0 && (
          <div className="mt-3 space-y-2">
            {devices.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {displayNameForDevice(d, allDevices)}
                  </p>
                  <p className="text-xs text-gray-500">
                    Last used {formatDate(d.lastAuthenticatedDate)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void handleForget(d)}
                  disabled={forgettingId === d.id}
                  className="text-sm text-red-600 hover:underline disabled:opacity-50"
                >
                  {forgettingId === d.id ? 'Forgetting…' : 'Forget'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* All admin devices (cross-account) */}
      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">All Devices</h2>
        <p className="mt-1 text-sm text-gray-500">
          Every trusted device across both admin accounts. Give a device a friendly name, or forget
          one on the other admin&apos;s behalf if it&apos;s lost or no longer used.
        </p>

        {allDevicesError && <p className="mt-3 text-sm text-red-700">{allDevicesError}</p>}
        {allDevices === null && !allDevicesError && (
          <p className="mt-3 text-sm text-gray-500">Loading…</p>
        )}
        {allDevices !== null && allDevices.length === 0 && (
          <p className="mt-3 text-sm text-gray-400">No remembered devices yet.</p>
        )}

        {allDevices !== null && allDevices.length > 0 && (
          <div className="mt-3 space-y-2">
            {allDevices.map((d) => {
              const draft = labelDrafts[d.deviceKey] ?? '';
              const dirty = draft !== (d.label ?? '');
              return (
                <div key={d.deviceKey} className="rounded-md border border-gray-200 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                      {d.ownerEmail}
                    </p>
                    <button
                      type="button"
                      onClick={() => void handleAdminForget(d)}
                      disabled={forgettingKey === d.deviceKey}
                      className="text-sm text-red-600 hover:underline disabled:opacity-50"
                    >
                      {forgettingKey === d.deviceKey ? 'Forgetting…' : 'Forget'}
                    </button>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="text"
                      value={draft}
                      onChange={(e) =>
                        setLabelDrafts((prev) => ({ ...prev, [d.deviceKey]: e.target.value }))
                      }
                      placeholder={d.deviceName}
                      maxLength={200}
                      className="block w-full max-w-sm rounded-md border border-gray-300 px-2.5 py-1.5 text-sm shadow-sm focus:border-phoenix-blue-500 focus:outline-none focus:ring-1 focus:ring-phoenix-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => void handleSaveLabel(d)}
                      disabled={!dirty || savingKey === d.deviceKey}
                      className="shrink-0 rounded-md bg-phoenix-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
                    >
                      {savingKey === d.deviceKey ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-400">{d.deviceName}</p>
                  <p className="text-xs text-gray-500">
                    Last used {formatIsoDate(d.lastAuthenticatedDate)}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
